import { useEffect, useRef, useState } from "react";
import { apiClient, readError } from "@/lib/api";
import { toast } from "sonner";
import {
  Camera, FileText, Trash2, Upload, X, Image as ImageIcon,
  Hammer, Wrench, Flag, CheckCircle2, ShieldCheck, Clock, Video, PlayCircle,
} from "lucide-react";

const STAGES = [
  { key: "brief", label: "Avant travaux", icon: Camera, color: "bg-zinc-700 text-white border-zinc-800" },
  { key: "demarrage", label: "Démarrage", icon: Flag, color: "bg-sky-600 text-white border-sky-700" },
  { key: "en_cours", label: "En cours", icon: Wrench, color: "bg-amber-500 text-[#0A0A0B] border-amber-600" },
  { key: "etape_cle", label: "Étape clé", icon: Hammer, color: "bg-indigo-600 text-white border-indigo-700" },
  { key: "finition", label: "Finition", icon: CheckCircle2, color: "bg-emerald-600 text-white border-emerald-700" },
  { key: "validation", label: "Validation", icon: ShieldCheck, color: "bg-[#FF4D00] text-white border-[#FF4D00]" },
];

const stageMeta = (k) => STAGES.find((s) => s.key === k) || STAGES[0];

const fmtDate = (iso) =>
  new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

export function MissionJournal({ missionId, mission, user, onUpdated }) {
  const [items, setItems] = useState([]);
  const [canValidate, setCanValidate] = useState(false);
  const [finitionCount, setFinitionCount] = useState(0);
  const [stage, setStage] = useState(() => (user?.id === mission?.demandeur_id ? "brief" : "demarrage"));
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null); // {url, mime, name}
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const videoCamRef = useRef(null);
  const videoLibRef = useRef(null);
  const [authedUrls, setAuthedUrls] = useState({}); // {update_id: blob_url}

  const isAdmin = user?.role === "admin";
  const isOwner = user?.id === mission?.demandeur_id;
  const isSelectedArtisan = user?.id === mission?.selected_artisan_id;
  const isInvited = (mission?.invited_artisans || []).includes(user?.id);
  const canPost = isAdmin || isOwner || isSelectedArtisan || isInvited;

  const load = async () => {
    try {
      const { data } = await apiClient.get(`/missions/${missionId}/updates`);
      setItems(data.updates || []);
      setCanValidate(!!data.can_validate);
      setFinitionCount(data.finition_count || 0);
      onUpdated?.({ can_validate: !!data.can_validate, finition_count: data.finition_count || 0 }, data.updates || []);
    } catch (e) {
      if (e?.response?.status !== 403) toast.error(readError(e));
    }
  };

  useEffect(() => {
    load();
    // Cleanup blob URLs on unmount
    return () => {
      Object.values(authedUrls).forEach((url) => URL.revokeObjectURL(url));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [missionId]);

  // Load thumbnails via authenticated XHR → blob → object URL
  useEffect(() => {
    let cancelled = false;
    items.forEach(async (it) => {
      if (it.file_type !== "image" && it.file_type !== "video") return;
      if (authedUrls[it.id]) return;
      try {
        const res = await apiClient.get(`/missions/${missionId}/updates/${it.id}/file`, {
          responseType: "blob",
        });
        if (cancelled) return;
        const url = URL.createObjectURL(res.data);
        setAuthedUrls((u) => ({ ...u, [it.id]: url }));
      } catch {
        /* ignore */
      }
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, missionId]);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const isVideo = file.type.startsWith("video/");
    const isImage = file.type.startsWith("image/");
    const isPdf = file.type === "application/pdf";
    const maxSize = isVideo ? 50 * 1024 * 1024 : 10 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error(`Fichier trop volumineux (max ${isVideo ? 50 : 10} Mo)`);
      e.target.value = "";
      return;
    }
    if (!(isImage || isPdf || isVideo)) {
      toast.error("Acceptés : JPG/PNG/WEBP/HEIC, PDF ou vidéo MP4/MOV/WEBM");
      e.target.value = "";
      return;
    }
    setBusy(true);
    const fd = new FormData();
    fd.append("stage", stage);
    if (caption.trim()) fd.append("caption", caption.trim());
    fd.append("file", file);
    try {
      await apiClient.post(`/missions/${missionId}/updates`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(isVideo ? "Vidéo publiée" : "Mise à jour publiée");
      setCaption("");
      if (fileRef.current) fileRef.current.value = "";
      if (cameraRef.current) cameraRef.current.value = "";
      if (videoCamRef.current) videoCamRef.current.value = "";
      if (videoLibRef.current) videoLibRef.current.value = "";
      await load();
    } catch (err) {
      toast.error(readError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (it) => {
    if (!window.confirm("Supprimer cette pièce ?")) return;
    try {
      await apiClient.delete(`/missions/${missionId}/updates/${it.id}`);
      toast.success("Supprimé");
      if (authedUrls[it.id]) URL.revokeObjectURL(authedUrls[it.id]);
      setAuthedUrls((u) => {
        const c = { ...u };
        delete c[it.id];
        return c;
      });
      await load();
    } catch (e) {
      toast.error(readError(e));
    }
  };

  const openPdf = async (it) => {
    try {
      const res = await apiClient.get(`/missions/${missionId}/updates/${it.id}/file`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(readError(e));
    }
  };

  return (
    <div className="border-2 border-[#0A0A0B] bg-white" data-testid="mission-journal">
      <div className="border-b-2 border-[#0A0A0B] px-6 py-4 flex items-center justify-between flex-wrap gap-3">
        <div className="display text-2xl flex items-center gap-2">
          <Camera className="w-5 h-5 text-[#FF4D00]" /> Journal de chantier
        </div>
        <div className="flex items-center gap-2 text-xs mono uppercase tracking-widest">
          {finitionCount > 0 ? (
            <span className="badge-pill bg-emerald-50 text-emerald-700 border-emerald-300">
              ✓ {finitionCount} photo{finitionCount > 1 ? "s" : ""} de finition
            </span>
          ) : (
            <span className="badge-pill bg-zinc-100 text-zinc-600 border-zinc-300">
              <Clock className="w-3 h-3 inline mr-1" /> Aucune finition publiée
            </span>
          )}
        </div>
      </div>

      {/* Upload form */}
      {canPost && (
        <div className="border-b-2 border-zinc-200 p-6 bg-zinc-50">
          <div className="mono text-[11px] uppercase tracking-widest text-zinc-500 mb-3">
            // Publier une mise à jour
          </div>
          <div className="grid md:grid-cols-5 gap-2">
            {STAGES.map((s) => {
              const Icon = s.icon;
              const active = stage === s.key;
              return (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => setStage(s.key)}
                  className={`border-2 px-3 py-2 mono text-[10px] uppercase tracking-widest font-semibold flex items-center justify-center gap-1.5 transition ${
                    active
                      ? s.color
                      : "bg-white text-[#0A0A0B] border-zinc-300 hover:border-[#0A0A0B]"
                  }`}
                  data-testid={`journal-stage-${s.key}`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {s.label}
                </button>
              );
            })}
          </div>
          <input
            type="text"
            placeholder="Commentaire (optionnel)"
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            maxLength={500}
            className="w-full mt-3 border-2 border-[#0A0A0B] px-4 py-2 bg-white text-sm"
            data-testid="journal-caption"
          />
          <label className="block mt-3">
            <input
              ref={fileRef}
              type="file"
              accept="image/*,application/pdf,video/*"
              onChange={handleFile}
              disabled={busy}
              className="hidden"
              data-testid="journal-file-input"
            />
            <input
              ref={cameraRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleFile}
              disabled={busy}
              className="hidden"
              data-testid="journal-camera-input"
            />
            <input
              ref={videoCamRef}
              type="file"
              accept="video/*"
              capture="environment"
              onChange={handleFile}
              disabled={busy}
              className="hidden"
              data-testid="journal-video-camera-input"
            />
            <input
              ref={videoLibRef}
              type="file"
              accept="video/*"
              onChange={handleFile}
              disabled={busy}
              className="hidden"
              data-testid="journal-video-library-input"
            />
            <div className="grid sm:grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => cameraRef.current?.click()}
                disabled={busy}
                className="btn-batisur flex items-center justify-center gap-2 disabled:opacity-60"
                data-testid="journal-take-photo-btn"
              >
                <Camera className="w-4 h-4" /> Prendre une photo
              </button>
              <button
                type="button"
                onClick={() => videoCamRef.current?.click()}
                disabled={busy}
                className="border-2 border-[#0A0A0B] bg-white text-[#0A0A0B] px-5 py-3 font-semibold text-sm uppercase tracking-widest hover:bg-[#FF4D00] hover:text-white hover:border-[#FF4D00] transition flex items-center justify-center gap-2 disabled:opacity-60"
                data-testid="journal-film-video-btn"
              >
                <Video className="w-4 h-4" /> Filmer une vidéo
              </button>
            </div>
            <div className="grid sm:grid-cols-2 gap-2 mt-2">
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={busy}
                className="border-2 border-zinc-300 bg-white text-zinc-700 px-4 py-2.5 text-xs uppercase tracking-widest hover:border-[#0A0A0B] transition flex items-center justify-center gap-2 disabled:opacity-60"
                data-testid="journal-upload-btn"
              >
                <Upload className="w-3.5 h-3.5" /> {busy ? "Envoi…" : "Choisir un fichier (photo/PDF/vidéo)"}
              </button>
              <button
                type="button"
                onClick={() => videoLibRef.current?.click()}
                disabled={busy}
                className="border-2 border-zinc-300 bg-white text-zinc-700 px-4 py-2.5 text-xs uppercase tracking-widest hover:border-[#0A0A0B] transition flex items-center justify-center gap-2 disabled:opacity-60"
                data-testid="journal-video-library-btn"
              >
                <PlayCircle className="w-3.5 h-3.5" /> Choisir une vidéo (galerie)
              </button>
            </div>
            <div className="mono text-[10px] uppercase tracking-widest text-zinc-500 mt-2 text-center">
              Photos & PDF : 10 Mo max · Vidéos : 50 Mo max · Compression photo automatique
            </div>
          </label>
        </div>
      )}

      {/* Timeline */}
      {items.length === 0 ? (
        <div className="p-10 text-center text-zinc-500 text-sm" data-testid="journal-empty">
          <ImageIcon className="w-8 h-8 mx-auto text-zinc-300" />
          <div className="mt-2">Aucune mise à jour publiée pour le moment</div>
        </div>
      ) : (
        <ul className="divide-y-2 divide-zinc-100">
          {items.map((it) => {
            const meta = stageMeta(it.stage);
            const Icon = meta.icon;
            const canDelete =
              user?.role === "admin" ||
              (user?.id === it.uploaded_by &&
                (Date.now() - new Date(it.created_at).getTime()) < 24 * 3600 * 1000);
            return (
              <li key={it.id} className="p-6 flex gap-5" data-testid={`journal-item-${it.id}`}>
                <div className="flex-shrink-0">
                  {it.file_type === "image" ? (
                    authedUrls[it.id] ? (
                      <img
                        src={authedUrls[it.id]}
                        alt={it.caption || it.original_name}
                        onClick={() => setPreview({ url: authedUrls[it.id], mime: "image/jpeg", name: it.caption })}
                        className="w-32 h-32 object-cover border-2 border-[#0A0A0B] cursor-zoom-in"
                        data-testid={`journal-thumb-${it.id}`}
                      />
                    ) : (
                      <div className="w-32 h-32 bg-zinc-100 border-2 border-zinc-200 flex items-center justify-center">
                        <ImageIcon className="w-6 h-6 text-zinc-400" />
                      </div>
                    )
                  ) : it.file_type === "video" ? (
                    authedUrls[it.id] ? (
                      <button
                        type="button"
                        onClick={() => setPreview({ url: authedUrls[it.id], mime: it.mime_type || "video/mp4", name: it.caption, isVideo: true })}
                        className="relative w-32 h-32 bg-[#0A0A0B] border-2 border-[#0A0A0B] cursor-zoom-in overflow-hidden group"
                        data-testid={`journal-video-thumb-${it.id}`}
                      >
                        <video src={authedUrls[it.id]} muted playsInline className="w-full h-full object-cover" preload="metadata" />
                        <div className="absolute inset-0 flex items-center justify-center bg-black/40 group-hover:bg-black/20 transition">
                          <PlayCircle className="w-10 h-10 text-white" strokeWidth={1.5} />
                        </div>
                        <span className="absolute bottom-1 right-1 mono text-[9px] uppercase tracking-widest bg-[#FF4D00] text-white px-1.5 py-0.5">Vidéo</span>
                      </button>
                    ) : (
                      <div className="w-32 h-32 bg-zinc-900 border-2 border-[#0A0A0B] flex items-center justify-center">
                        <Video className="w-8 h-8 text-white/50" />
                      </div>
                    )
                  ) : (
                    <button
                      type="button"
                      onClick={() => openPdf(it)}
                      className="w-32 h-32 bg-zinc-50 border-2 border-[#0A0A0B] flex flex-col items-center justify-center gap-2 hover:bg-zinc-100"
                      data-testid={`journal-pdf-${it.id}`}
                    >
                      <FileText className="w-8 h-8 text-[#FF4D00]" />
                      <span className="mono text-[10px] uppercase tracking-widest">Ouvrir PDF</span>
                    </button>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`mono text-[10px] uppercase tracking-widest px-2 py-1 border-2 font-semibold flex items-center gap-1 ${meta.color}`}>
                      <Icon className="w-3 h-3" /> {meta.label}
                    </span>
                    <span className="text-xs text-zinc-500 mono">{fmtDate(it.created_at)}</span>
                  </div>
                  <div className="mt-2 text-sm">
                    <strong>{it.uploaded_by_name}</strong>
                    <span className="text-zinc-500 mono text-[10px] uppercase tracking-widest ml-2">{it.uploaded_by_role}</span>
                  </div>
                  {it.caption && <p className="mt-2 text-sm text-zinc-700 whitespace-pre-line">{it.caption}</p>}
                  {it.original_name && it.file_type === "pdf" && (
                    <div className="mt-2 text-xs text-zinc-500 mono truncate">📎 {it.original_name}</div>
                  )}
                </div>
                {canDelete && (
                  <button
                    onClick={() => remove(it)}
                    className="self-start text-zinc-400 hover:text-red-600 transition"
                    title="Supprimer"
                    data-testid={`journal-delete-${it.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {/* Lightbox */}
      {preview && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-6"
          onClick={() => setPreview(null)}
          data-testid="journal-lightbox"
        >
          <button
            type="button"
            onClick={() => setPreview(null)}
            className="absolute top-6 right-6 text-white hover:text-[#FF4D00]"
          >
            <X className="w-8 h-8" />
          </button>
          {preview.isVideo ? (
            <video
              src={preview.url}
              controls
              autoPlay
              playsInline
              className="max-h-full max-w-full"
              onClick={(e) => e.stopPropagation()}
              data-testid="journal-lightbox-video"
            />
          ) : (
            <img src={preview.url} alt={preview.name || ""} className="max-h-full max-w-full object-contain" />
          )}
          {preview.name && (
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white text-sm bg-black/60 px-4 py-2 max-w-md text-center">
              {preview.name}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default MissionJournal;
